// Passwords for signing in with email and password (D393). PBKDF2-SHA256 through WebCrypto, so the same code runs in
// the Worker and in Node (the CI scripts that seed the QA accounts). 100,000 iterations is the most Workers allow.

const enc = new TextEncoder();
const b64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));
const unb64 = (s: string) => Uint8Array.from(atob(s), c => c.charCodeAt(0));

export const PASSWORD_ITERATIONS = 100_000;
export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 128;
/** How long a set-password link stays valid: sent at approval, or asked for to reset a password. */
export const PASSWORD_LINK_TTL_MS = { invite: 7 * 24 * 3_600_000, reset: 3_600_000 } as const;
export type PasswordLinkPurpose = keyof typeof PASSWORD_LINK_TTL_MS;

/** Passwords too common to allow, whatever their length (the start of breached-password lists, lengthened). */
const COMMON = new Set([
  '123456789012',
  '1234567890123',
  'qwertyuiopas',
  'passwordpassword',
  'password1234',
  'password12345',
  'iloveyou1234',
  'administrator',
  'letmein12345',
  'welcome12345',
  'changeme1234',
  'hoopoehoopoe',
  'qwerty123456',
  'aaaaaaaaaaaa',
]);

/** What is wrong with a new password, in words for the form; empty when it can be used. */
export function passwordProblems(password: string, email = ''): string[] {
  const problems: string[] = [];
  const chars = [...password];
  if (chars.length < PASSWORD_MIN) problems.push(`Use at least ${PASSWORD_MIN} characters.`);
  if (chars.length > PASSWORD_MAX) problems.push(`Use at most ${PASSWORD_MAX} characters.`);
  const folded = password.toLowerCase();
  const local = email.toLowerCase().split('@')[0] ?? '';
  if (folded && (COMMON.has(folded) || new Set(chars).size <= 2)) problems.push('This password is too easy to guess.');
  else if (local.length >= 4 && folded.includes(local)) problems.push('Don’t use your email address in the password.');
  return problems;
}

async function derive(password: string, salt: Uint8Array, iterations: number): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', enc.encode(password.normalize('NFKC')), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations }, key, 256);
  return new Uint8Array(bits);
}

/** `pbkdf2-sha256$<iterations>$<salt>$<hash>` with a random 16-byte salt. */
export async function hashPassword(password: string, iterations = PASSWORD_ITERATIONS): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  return `pbkdf2-sha256$${iterations}$${b64(salt)}$${b64(await derive(password, salt, iterations))}`;
}

/** Compares in constant time; false for a missing or unreadable hash. */
export async function verifyPassword(password: string, stored: string | null | undefined): Promise<boolean> {
  const [scheme, iter, salt, hash] = (stored ?? '').split('$');
  const iterations = Number(iter);
  if (scheme !== 'pbkdf2-sha256' || !salt || !hash || !Number.isInteger(iterations) || iterations < 1 || iterations > PASSWORD_ITERATIONS)
    return false;
  const expected = unb64(hash);
  const got = await derive(password, unb64(salt), iterations);
  if (got.length !== expected.length) return false;
  let diff = 0;
  for (let i = 0; i < got.length; i++) diff |= (got[i] ?? 0) ^ (expected[i] ?? 0);
  return diff === 0;
}

/**
 * The QA accounts' password on deployed environments, derived from the QA seed both the deploy (which stores its
 * hash) and the smoke (which signs in with it) already hold, so no new secret is needed.
 */
export async function qaPassword(seed: string, email: string): Promise<string> {
  const key = await crypto.subtle.importKey('raw', enc.encode(seed), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const mac = new Uint8Array(await crypto.subtle.sign('HMAC', key, enc.encode(`qa-password\u0000${email.toLowerCase()}`)));
  return `qa-${b64(mac)
    .replace(/[^A-Za-z0-9]/g, '')
    .slice(0, 32)}`;
}
